/**
 * Контракты режима «Мои ошибки» (ТЗ §6, режим 4; US-03).
 *
 * Режим отличается от тренировки не материалом, а формой: вместо сплошного
 * упражнения пользователь проходит полный Memory Loop по каждому слову —
 * увидеть, разбить, скрыть, напечатать, применить (ТЗ §7.4).
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  MasteryUnitId,
  TypingError,
  TypingMetrics,
} from '@type2x/shared-contracts';
import type { MemoryLoopStage, MemoryLoopState } from '@type2x/learning-engine';
import type { RunObservation } from '@type2x/typing-engine';

/** Снимок навыка, из которого отбираются проблемные слова. */
export interface MistakeSnapshot {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly stats: Readonly<Record<string, MasteryStat>>;
}

/** Наблюдения за одним проходом стадии. */
export type StageObservation = RunObservation;

/** Прогон одной стадии: что пользователь набрал и с каким результатом. */
export interface StageRun {
  readonly observation: StageObservation;
  readonly metrics: TypingMetrics;
  readonly errors: readonly TypingError[];
}

/** Работа над одним проблемным словом. */
export interface MistakeDrill {
  readonly unitId: MasteryUnitId;
  readonly word: string;
  /** Части слова для стадии «разбить». */
  readonly chunks: readonly string[];
  /**
   * Естественное предложение со словом либо null.
   *
   * Словарь предложений покрывает не каждое слово, а выдумывать предложение
   * нельзя: машинная конструкция не тренирует реальный набор (ТЗ §8.3).
   * Слово без предложения проходит цикл без стадии «применить».
   */
  readonly sentence: string | null;
  readonly loop: MemoryLoopState;
  /** Сколько раз слово показывалось за микросессию. */
  readonly shows: number;
  /** Цикл дошёл до отложенного возврата: слово освоено. */
  readonly completed: boolean;
  /** Предел показов исчерпан: слово отложено до следующего раза. */
  readonly deferred: boolean;
  /** Накопленные наблюдения по слову за микросессию. */
  readonly observed: StageObservation;
}

/** Микросессия работы над ошибками. */
export interface MistakesSession {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly drills: readonly MistakeDrill[];
  /** С каким словом пользователь работает сейчас. */
  readonly currentIndex: number;
  /** Все слова освоены либо отложены: микросессия закончена. */
  readonly finished: boolean;
  /** Суммарные метрики набора за микросессию. */
  readonly metrics: TypingMetrics;
  /** Ошибки всех прогонов: из них собирается сводка по типам для сервера. */
  readonly errors: readonly TypingError[];
}

/** Задание текущей стадии. */
export interface StageTask {
  /**
   * Стадия задания: только та, на которой печатают.
   *
   * Стадия отложенного возврата заданием не бывает — слово к этому моменту
   * покидает микросессию (ТЗ §7.4). Пока тип был шире, экран собирал по ней
   * подпись, которой в словаре нет.
   */
  readonly stage: TypedStage;
  readonly word: string;
  readonly chunks: readonly string[];
  /** Текст, который набирает пользователь. */
  readonly text: string;
  readonly hintLevel: HintLevel;
  /** Показывать ли разбиение слова на части. */
  readonly showsChunks: boolean;
}

/** Сколько слов берётся в одну микросессию. */
export const MISTAKES_CAPACITY = 5;

/**
 * Наименьшая длина материала микросессии, символов.
 *
 * Цикл разбирает слово по шагам: показать, разбить на части, скрыть, набрать
 * по памяти, применить в предложении. У одиночного нажатия нет ни частей, ни
 * применения — «работаем над словом „в“» читается как насмешка, а не как
 * задание.
 *
 * Слабая одиночная клавиша не остаётся без внимания: ею занимаются «Основы»
 * и «Цифры», которые для того и открывают клавиши группами (ТЗ FR-EN-67).
 */
export const MIN_DRILL_LENGTH = 2;

/**
 * Сколько раз подряд допустимо показать одно слово.
 *
 * Упереться в одно слово — верный способ закрепить раздражение, а не навык:
 * после трёх подходов работа переходит к следующему, а слово возвращается
 * позже (ТЗ §7.4).
 */
export const MAX_CONSECUTIVE_SHOWS = 3;

/**
 * Сколько всего показов слова допускает одна микросессия.
 *
 * Полный цикл занимает пять успешных стадий, запас оставлен на ошибки. Без
 * этого предела слово, которое не даётся сегодня, крутилось бы бесконечно:
 * микросессия обязана заканчиваться.
 */
export const MAX_SHOWS_PER_DRILL = 8;

/** Стадии, на которых пользователь действительно печатает. */
export const TYPED_STAGES = [
  'see',
  'split',
  'hide',
  'type',
  'apply',
] as const satisfies readonly MemoryLoopStage[];

/** Стадия, на которой печатают: всё, кроме отложенного возврата. */
export type TypedStage = (typeof TYPED_STAGES)[number];

/** Печатают ли на этой стадии. */
export function isTypedStage(stage: MemoryLoopStage): stage is TypedStage {
  return (TYPED_STAGES as readonly MemoryLoopStage[]).includes(stage);
}
